/* ============================================================
   JOWI — popup-triggers.js (portado de Lindo, 04/10/2026)
   Frequency cap (localStorage) · time / scroll / exit-intent triggers
   Focus trap · Escape close · GA4 analytics dispatch
   ============================================================ */
(function () {
  'use strict';

  const STORAGE_KEY = 'mga_popup_state_v1';
  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;

  // ---------------- State ----------------
  function loadState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}'); }
    catch { return {}; }
  }
  function saveState(s) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch {}
  }

  function shouldShow(id, frequency) {
    const state = loadState();
    const entry = state[id];
    if (!entry) return true;
    const now = Date.now();
    switch (frequency) {
      case 'always': return true;
      case 'once-per-session':
        return !sessionStorage.getItem(`mga_popup_session_${id}`);
      case 'once-per-day':
        return (now - entry.lastShown) > 24 * 3600 * 1000;
      case 'once-per-week':
        return (now - entry.lastShown) > 7 * 24 * 3600 * 1000;
      default: return true;
    }
  }

  function markShown(id) {
    const state = loadState();
    state[id] = { lastShown: Date.now() };
    saveState(state);
    sessionStorage.setItem(`mga_popup_session_${id}`, '1');
  }

  // ---------------- Focus trap ----------------
  function trapFocus(container) {
    const focusables = container.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (!focusables.length) return () => {};
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  // ---------------- Popup controller ----------------
  class Popup {
    constructor(el) {
      this.el = el;
      this.id = el.dataset.popupId;
      this.trigger = el.dataset.trigger;
      this.device = el.dataset.device || 'all';
      this.frequency = el.dataset.frequency || 'once-per-session';
      this.delay = parseInt(el.dataset.delay, 10) || 0;
      this.scrollPercent = parseInt(el.dataset.scrollPercent, 10) || 0;
      this.releaseFocus = null;
      this.shown = false;

      el.addEventListener('click', (e) => {
        if (e.target.matches('[data-popup-close]') || e.target.closest('[data-popup-close]')) {
          e.preventDefault();
          this.close('close-button');
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && el.getAttribute('aria-hidden') === 'false') this.close('escape');
      });

      // Form submission
      const form = el.querySelector('[data-popup-form]');
      if (form) {
        form.addEventListener('submit', (e) => {
          e.preventDefault();
          this.submit(form);
        });
      }

      this.scheduleTrigger();
    }

    scheduleTrigger() {
      if (this.device === 'desktop' && isMobile()) return;
      if (this.device === 'mobile' && !isMobile()) return;
      if (!shouldShow(this.id, this.frequency)) return;

      switch (this.trigger) {
        case 'time':
          setTimeout(() => this.open('time'), this.delay * 1000);
          break;
        case 'scroll':
          this.scrollListener = () => {
            const pct = (window.scrollY + window.innerHeight) / document.documentElement.scrollHeight * 100;
            if (pct >= this.scrollPercent) {
              window.removeEventListener('scroll', this.scrollListener);
              this.open('scroll');
            }
          };
          window.addEventListener('scroll', this.scrollListener, { passive: true });
          break;
        case 'exit-intent':
          if (isMobile()) return;
          this.exitListener = (e) => {
            if (e.clientY <= 0 && !this.shown) {
              // sin quitar el listener: si había otro popup abierto, lo intenta en la siguiente salida
              this.open('exit-intent');
            }
          };
          document.addEventListener('mouseleave', this.exitListener);
          break;
        case 'always':
          /* permanent (e.g. WhatsApp FAB handled elsewhere) */
          break;
      }
    }

    open(triggerSource) {
      if (this.shown) return;
      // 01/10/2026 · Nunca dos popups a la vez: si hay otro abierto, este espera a que se cierre.
      const other = document.querySelector('[data-popup][aria-hidden="false"]');
      if (other && other !== this.el) {
        if (triggerSource === 'exit-intent') return;       // la salida no se encola: se pierde esta vez
        clearTimeout(this.waitTimer);
        this.waitTimer = setTimeout(() => this.open(triggerSource), 4000);
        return;
      }
      // Quien ya se suscribió en un popup no vuelve a ver otro con formulario.
      try { if (localStorage.getItem('jowi_popup_subscribed') === '1' && this.el.querySelector('[data-popup-form]')) return; } catch (e) {}
      this.shown = true;
      this.lastFocused = document.activeElement;
      this.el.setAttribute('aria-hidden', 'false');
      document.body.classList.add('popup-open');
      this.releaseFocus = trapFocus(this.el);
      const firstInput = this.el.querySelector('input:not([type="hidden"]):not([type="checkbox"]), button:not([data-popup-close])');
      if (firstInput) firstInput.focus();
      markShown(this.id);

      // Lazy-load video sources
      this.el.querySelectorAll('[data-popup-video-src]').forEach((v) => {
        if (!v.src && !v.querySelector('source[src]')) {
          v.src = v.dataset.popupVideoSrc;
        }
      });

      this.track('popup_show', { trigger: triggerSource });
    }

    close(reason) {
      this.el.setAttribute('aria-hidden', 'true');
      if (!document.querySelector('[data-popup][aria-hidden="false"]')) document.body.classList.remove('popup-open');
      if (this.releaseFocus) this.releaseFocus();
      if (this.lastFocused) this.lastFocused.focus();
      this.track('popup_close', { reason });

      // Stop video
      this.el.querySelectorAll('iframe[data-popup-video-src]').forEach((iframe) => {
        iframe.src = '';
      });
      this.el.querySelectorAll('video[data-popup-video-src]').forEach((video) => {
        video.pause();
      });
    }

    errorBox(form) {
      let b = form.querySelector('.popup__error');
      if (!b) {
        b = document.createElement('p');
        b.className = 'popup__error';
        b.setAttribute('role', 'alert');
        b.style.cssText = 'color:#B3261E;font-size:13px;line-height:1.35;margin:6px 0 8px;';
        b.hidden = true;
        const btn = form.querySelector('[type="submit"]');
        if (btn && btn.parentNode) btn.parentNode.insertBefore(b, btn); else form.appendChild(b);
      }
      return b;
    }

    async submit(form) {
      // Validar ANTES de enviar. El formulario lleva novalidate y sin esto salía
      // un email vacío y el popup daba éxito y enseñaba el código igualmente.
      const L = window.themeLocales || {};
      const errBox = this.errorBox(form);
      errBox.hidden = true;
      const tel = form.querySelector('input[name="phone"]');
      if (tel) tel.setCustomValidity('');   // limpiar el aviso del intento anterior
      if (!form.checkValidity()) { form.reportValidity(); return; }
      if (tel && tel.required && tel.value.replace(/\D/g, '').length < 10) {
        tel.setCustomValidity(L.popup_phone || 'Please enter a valid mobile number.');
        tel.reportValidity();
        tel.addEventListener('input', () => tel.setCustomValidity(''), { once: true });
        return;
      }
      const formData = new FormData(form);
      const payload = Object.fromEntries(formData.entries());
      const provider = form.dataset.popupProvider;

      const submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.dataset.originalLabel = submitBtn.textContent;
        submitBtn.textContent = '…';
      }

      this.track('popup_submit', { popup: this.id, provider });

      try {
        await (window.ShopThemePopupAPI || {}).submit?.(provider, this.id, payload);
        try { localStorage.setItem('jowi_popup_subscribed', '1'); } catch (e) {}
        const success = form.querySelector('[data-popup-success]');
        if (success) {
          success.hidden = false;
          // El botón de enviar se esconde: si no, se queda en «…» para siempre.
          // Y lo que va DENTRO del éxito (el botón que copia el código) no se
          // desactiva: antes salía apagado y no se podía copiar (25/09/2026).
          if (submitBtn) submitBtn.hidden = true;
          form.querySelectorAll('input, button:not([data-popup-close])').forEach((el) => {
            if (!success.contains(el)) el.disabled = true;
          });
        } else {
          // Auto-close after success
          setTimeout(() => this.close('success'), 1500);
        }
      } catch (err) {
        console.error('[popup] submit failed', err);
        errBox.textContent = L.popup_error || 'We could not save your details. Please try again.';
        errBox.hidden = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = submitBtn.dataset.originalLabel;
        }
      }
    }

    track(event, data) {
      if (window.gtag) {
        window.gtag('event', event, { popup_id: this.id, ...data });
      }
      document.dispatchEvent(new CustomEvent('popup:' + event, { detail: { id: this.id, ...data } }));
    }
  }

  // ---------------- Survey controller ----------------
  function initSurvey(form) {
    const steps = Array.from(form.querySelectorAll('[data-survey-step]'));
    const progress = form.querySelector('[data-survey-progress]');
    let current = 0;
    const total = steps.length;

    const updateProgress = () => {
      if (progress) progress.style.width = `${((current + 1) / total) * 100}%`;
    };
    const showStep = (i) => {
      steps.forEach((s, idx) => s.hidden = idx !== i);
      current = i;
      updateProgress();
    };

    form.querySelectorAll('[data-survey-next]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const stepEl = steps[current];
        const radios = stepEl.querySelectorAll('input[type="radio"]');
        const selected = Array.from(radios).some((r) => r.checked);
        if (!selected) {
          stepEl.classList.add('shake');
          setTimeout(() => stepEl.classList.remove('shake'), 400);
          return;
        }
        if (current < total - 1) showStep(current + 1);
      });
    });
    form.querySelectorAll('[data-survey-prev]').forEach((btn) => {
      btn.addEventListener('click', () => { if (current > 0) showStep(current - 1); });
    });
    updateProgress();
  }

  // ---------------- Init ----------------
  const popups = {};
  document.querySelectorAll('[data-popup]').forEach((el) => { const p = new Popup(el); popups[p.id] = p; });
  document.querySelectorAll('[data-survey-form]').forEach(initSurvey);

  // Enlaces «#oferta» (cinta de anuncios): abren el popup de bienvenida, que da el código a cambio del email.
  // El código no se enseña fuera del popup. Sin popup en la página, el enlace sigue a su destino.
  document.addEventListener('click', (e) => {
    const a = e.target.closest && e.target.closest('a[href$="#oferta"]');
    const p = popups.welcome;
    if (!a || !p) return;
    e.preventDefault();
    p.shown = false;
    p.open('announcement');
  });

  // Expose for debugging
  window.ShopThemePopups = { loadState, saveState };
})();
